import React, { useEffect, useState } from 'react';
import { ErrorMessage, Field, Formik, Form } from 'formik';
import FormFieldWithLabel from '../../../common/FormFieldWithLabel';
import CustomSelect from '../../../../common/formElements/CustomSelect';
import {
  createActionSettingsSchema,
  getFieldInfoByName,
  getPageOption,
  userRolesOptions,
  validateFormDataSessionKey,
  validateResponseDataSessionKey,
} from '../collection/Util';
import CustomLoader from '../../../common/CustomLoader';
import BrowserStorageOptions from '../BrowserStorageOptions';
import ConsoleLogOptions from '../../ConsoleLogOptions';
import { getUserRoleFromApi } from '../../../../common/utils/item-util';
import { pluginCode } from 'drapcode-constant';

export const UserWithTenantsAndSettingEventFieldForm = ({
  projectId,
  selectedEvent,
  eventAction,
  actionData,
  events,
  actions,
  hideModal,
  showLoaderForAction,
  version,
}) => {
  // states
  const [userRoles, setUserRoles] = useState([]);
  const [showBSLOption, setShowBSLOption] = useState(false);
  const [showCLOption, setShowCLOption] = useState(false);
  // hooks
  useEffect(() => {
    getUserRoleFromApi(projectId, version).then((res) => {
      setUserRoles(res);
    });
  }, []);
  // utils func
  const bslOptionHandler = () => {
    setShowBSLOption(!showBSLOption);
  };

  const clogOptionHandler = () => {
    setShowCLOption(!showCLOption);
  };

  const tenantsAndUserSettingCheckbox = (values) => {
    const { plugins } = events || '';
    const multiTenantPlugin =
      plugins && plugins.find((e) => e.code === pluginCode.MULTI_TENANT_SAAS);
    if (!multiTenantPlugin) return '';
    const {
      setting: { multiTenantCollection, subTenantCollection, userSettingsCollection },
    } = multiTenantPlugin;
    const fieldsOption = [];
    const { createTenant } = values;
    if (multiTenantCollection)
      fieldsOption.push({
        name: 'createTenant',
        label: 'Create Tenant with User',
        disabled: false,
      });
    if (subTenantCollection)
      fieldsOption.push({
        name: 'createSubTenant',
        label: 'Create Sub Tenant with User',
        disabled: !createTenant,
      });
    if (userSettingsCollection)
      fieldsOption.push({
        name: 'createUserSetting',
        label: 'Create User Setting with User',
        disabled: !createTenant,
      });

    return fieldsOption.map((field) => (
      <div key={field.name} className="col-12">
        <div className="form-group">
          <FormFieldWithLabel
            cta={false}
            label=""
            icon="boolean-toggle"
            infoIcon={true}
            infoDescription={getFieldInfoByName(field.name)}
          >
            <div className="form-check">
              <label className="form-check-label">
                <Field
                  type="checkbox"
                  name={field.name}
                  className="form-check-input"
                  title={field.name}
                  disabled={field.disabled}
                />
                {field.label}
                <span className="form-check-sign">
                  <span className="check"></span>
                </span>
              </label>
              <ErrorMessage name={field.name}>
                {(msg) => <p className="text-error">{msg}</p>}
              </ErrorMessage>
            </div>
          </FormFieldWithLabel>
        </div>
      </div>
    ));
  };

  const handleSubmit = (values) => {
    let parameters = [];
    let isCreateTenant = false;
    Object.keys(values).map((value) => {
      const action = actionData.args.find((param) => {
        return param.name === value;
      });
      let finalValue = values[value];
      if (value === 'createTenant') isCreateTenant = finalValue;
      if (['createSubTenant', 'createUserSetting'].includes(value)) {
        finalValue = isCreateTenant && finalValue;
      }
      parameters.push({ name: value, value: finalValue, type: action.type });
    });

    let actionFormData = eventAction;
    actionFormData.parameters = parameters;
    actions.updateAction({
      projectId,
      version,
      eventId: selectedEvent.uuid,
      data: actionFormData,
    });
    hideModal();
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={createActionSettingsSchema(actionData, eventAction)}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        handleSubmit(values);
      }}
    >
      {({ isSubmitting, values, setFieldValue, setFieldTouched, errors }) => (
        <Form>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <FormFieldWithLabel
                  cta={false}
                  label="User Role"
                  icon="select"
                  infoIcon={true}
                  infoDescription={getFieldInfoByName('userRole')}
                >
                  <CustomSelect
                    name={'userRole'}
                    isMulti={false}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    options={userRolesOptions(events, userRoles)}
                    value={values['userRole']}
                    formValues={values}
                    setFieldValue={setFieldValue}
                  />
                </FormFieldWithLabel>
              </div>
            </div>
            {tenantsAndUserSettingCheckbox(values)}
            <div className="col-12">
              <div className="form-group">
                <FormFieldWithLabel
                  cta={false}
                  label="Success Message"
                  icon="text"
                  infoIcon={true}
                  infoDescription={getFieldInfoByName('successMessage')}
                >
                  <Field
                    name="successMessage"
                    type="text"
                    placeholder="(Optional) Enter custom success message."
                    className="form-control form-control-sm"
                  />
                </FormFieldWithLabel>
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <FormFieldWithLabel
                  cta={false}
                  label="Error Message"
                  icon="text"
                  infoIcon={true}
                  infoDescription={getFieldInfoByName('errorMessage')}
                >
                  <Field
                    name="errorMessage"
                    type="text"
                    placeholder="(Optional) Enter custom error message."
                    className="form-control form-control-sm"
                  />
                </FormFieldWithLabel>
              </div>
            </div>
            <div className="form-group">
              <div className="col-12">
                <FormFieldWithLabel
                  cta={false}
                  label=""
                  icon="boolean-toggle"
                  infoIcon={true}
                  infoDescription={getFieldInfoByName('enableLoginUser')}
                >
                  <div className="form-check">
                    <label className="form-check-label">
                      <Field
                        type="checkbox"
                        name="enableLoginUser"
                        className="form-check-input"
                        title="enableLoginUser"
                      />
                      Login with recently created user
                      <span className="form-check-sign">
                        <span className="check"></span>
                      </span>
                    </label>
                    <ErrorMessage name="enableLoginUser">
                      {(msg) => <p className="text-error">{msg}</p>}
                    </ErrorMessage>
                  </div>
                </FormFieldWithLabel>
              </div>
            </div>
            {values.enableLoginUser && (
              <div className="col-12">
                <div className="form-group">
                  <FormFieldWithLabel
                    cta={false}
                    label="Page"
                    icon="select"
                    infoIcon={true}
                    infoDescription={getFieldInfoByName('redirectUrl')}
                  >
                    <CustomSelect
                      name={'redirectUrl'}
                      isMulti={false}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      options={getPageOption(events)}
                      value={values['redirectUrl']}
                      formValues={values}
                      setFieldValue={setFieldValue}
                      placeholder={'Select Page'}
                    />
                  </FormFieldWithLabel>
                </div>
              </div>
            )}
            <div className="col-12">
              <div className="form-group">
                <button
                  onClick={(e) => bslOptionHandler(e)}
                  className="btn btn-icon-only btn-outline-info ml-5 p-1"
                  title="Show Browser Storage Options"
                  type="button"
                >
                  <i className="wb webconnect-cog"></i>&nbsp;Browser Storage Options
                </button>
                <button
                  onClick={(e) => clogOptionHandler(e)}
                  className="btn btn-icon-only btn-outline-info ml-2 p-1"
                  title="Show Console Log Options"
                  type="button"
                >
                  <i className="wb webconnect-cog"></i>&nbsp;Console Log Options
                </button>
              </div>
              {showBSLOption && (
                <BrowserStorageOptions
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  validateFormDataSessionKey={validateFormDataSessionKey}
                  validateResponseDataSessionKey={validateResponseDataSessionKey}
                />
              )}
              {showCLOption && <ConsoleLogOptions values={values} />}
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 text-left">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={hideModal}
              >
                <span>
                  <i className="wb webconnect-times"></i>&nbsp;Cancel
                </span>
              </button>
            </div>
            <div className="col-md-6 text-right">
              <button type="submit" className="btn btn-theme-submit btn-sm">
                <i className="wb webconnect-save-regular"></i>&nbsp;Save&nbsp;
                <CustomLoader loading={showLoaderForAction} />
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
